import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import {
  Typography,
  Modal,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Box,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { RemoveCircle, HighlightOff } from '@mui/icons-material';
import { Button, GradientButton, Input, Loader } from '../../components';
import {
  useCreateThreatWithExtras,
  useDomainsListQuery,
} from '../../queries/threats';
import { useNotesValuesQuery } from '../../queries/noteComments';
import { useSourcesQuery, useThreatStatusesQuery } from '../../queries/lookups';
import NoteList from '../../components/NoteList/NoteList';
import {
  AddThreatsMode,
  CreateThreatModelProps,
  DomainThreats,
  Errors,
  SourcesType,
} from '../../helpers/types';
import { ButtonWrapper, CloseButton } from './Style/Home.style';
import { urlRegex, formatSourceName, getSourceColor } from '../../utils';
import MultiDomainInput from '../../components/MultiDomainInput/MultiDomainInput';
import BulkExplainSection from '../../components/ThreatExplain/BulkExplainSection';
import { BulkExplainSummary } from '../../services/threatExplainPersistence';
import { buildHomeBulkExplainPayload } from './buildHomeBulkExplainPayload';
import {
  InfoBox,
  InfoTypography,
  NotesContainer,
  ScrollableContent,
  SectionDivider,
  StyledBox,
  StyledButton,
  StyledTableContainer,
  StyledTextField,
  StyledTypography,
  WideModalWrapper,
} from './Style/CreateThreatModel.style';

const CreateThreatModel: React.FC<CreateThreatModelProps> = ({
  open,
  handleClose,
  userData,
}) => {
  const [mode, setMode] = useState<AddThreatsMode>('single');
  const [selectedProcess, setSelectedProcess] = useState<string>('w');
  const [selectedStatus, setSelectedStatus] = useState<string>('confirmed');
  const dropdownStates = useRef<boolean[]>([]);
  const isDomainRef = useRef(false);
  const [singleDomainThreats, setSingleDomainThreats] = useState<DomainThreats>(
    {
      protectedAssetId: '',
      protectedAssetName: '',
      companyId: '',
      threatName: '',
      createThreats: [],
    },
  );
  const [uniqueDomainThreats, setUniqueDomainThreats] = useState<
    DomainThreats[]
  >([
    {
      protectedAssetId: '',
      protectedAssetName: '',
      companyId: '',
      threatName: '',
      createThreats: [],
    },
  ]);
  const [errors, setErrors] = useState<Errors>({
    singleDomain: '',
    singleThreats: '',
    uniqueThreats: [],
  });
  const [selectedNoteIds, setSelectedNoteIds] = useState<number[]>([]);
  const [commentText, setCommentText] = useState<string>('');
  const [fnInformation, setFnInformation] = useState<string>('');
  const [aiSummary, setAiSummary] = useState<BulkExplainSummary | null>(null);

  const { createThreatWithExtras, isPending: isCreatingThreat } =
    useCreateThreatWithExtras();
  const { data: domainsListData } = useDomainsListQuery(
    { status: 'active' },
    open,
  );
  const protectedDomains = domainsListData?.Items;
  const { data: sourcesList } = useSourcesQuery();
  const { data: statusesData } = useThreatStatusesQuery();
  const statuses = useMemo(() => statusesData || [], [statusesData]);
  const { data: notesValues, isLoading: notesValuesLoading } =
    useNotesValuesQuery();

  const statusOptions = useMemo(
    () => (statuses || []).map((s: any) => s.name),
    [statuses],
  );

  const sourceOptions = (sourcesList || []).map((source: SourcesType) => ({
    value: source.code,
    label: formatSourceName(source.name),
    name: source.name,
  }));

  const falseNegativeSourceCode = useMemo(() => {
    const fnSource = (sourcesList || []).find(
      (source: SourcesType) => source.name === 'false_negative',
    );
    return fnSource?.code;
  }, [sourcesList]);

  const bulkExplainPayload = useMemo(
    () =>
      buildHomeBulkExplainPayload({
        mode,
        singleDomainThreats,
        uniqueDomainThreats,
        status: selectedStatus,
        commentText,
        fnInformation,
        selectedNoteIds,
        notesCatalog: notesValues ?? [],
        protectedDomains: protectedDomains ?? [],
      }),
    [
      mode,
      singleDomainThreats,
      uniqueDomainThreats,
      selectedStatus,
      commentText,
      fnInformation,
      selectedNoteIds,
      notesValues,
      protectedDomains,
    ],
  );

  // IDs for the bulk generation log row: only meaningful when the whole batch
  // shares one protected asset; mixed batches log null.
  const bulkExplainIds = useMemo(() => {
    const rows =
      mode === 'single' ? [singleDomainThreats] : uniqueDomainThreats;
    const assetIds = [
      ...new Set(rows.map(r => r.protectedAssetId).filter(Boolean)),
    ];
    const companyIds = [...new Set(rows.map(r => r.companyId).filter(Boolean))];
    return {
      protectedAssetId: assetIds.length === 1 ? Number(assetIds[0]) : null,
      companyId: companyIds.length === 1 ? Number(companyIds[0]) : null,
    };
  }, [mode, singleDomainThreats, uniqueDomainThreats]);

  const validateInputs = useCallback(() => {
    let isValid = true;

    setErrors({
      singleDomain: '',
      singleThreats: '',
      uniqueThreats: [],
    });

    if (mode === 'single') {
      if (!singleDomainThreats.protectedAssetName) {
        setErrors((prev: Errors) => ({
          ...prev,
          singleDomain: 'Domain is required.',
        }));
        isValid = false;
      }

      if (singleDomainThreats.createThreats.length === 0) {
        setErrors((prev: Errors) => ({
          ...prev,
          singleThreats: 'At least one threat name is required.',
        }));
        isValid = false;
      } else if (
        singleDomainThreats.createThreats.some(
          (threat: string) => !urlRegex.test(threat),
        )
      ) {
        setErrors((prev: Errors) => ({
          ...prev,
          singleThreats: 'All threat names must be valid URLs.',
        }));
        isValid = false;
      }
    } else {
      const uniqueThreatsErrors = uniqueDomainThreats.map(
        (threat: DomainThreats) => {
          const errors = { domain: '', threats: '' };

          if (!threat.protectedAssetName) {
            errors.domain = 'Domain is required.';
            isValid = false;
          }

          if (threat.createThreats.length === 0) {
            errors.threats = 'At least one threat name is required.';
            isValid = false;
          } else if (
            threat.createThreats.some((name: string) => !urlRegex.test(name))
          ) {
            errors.threats = 'All threat names must be valid URLs.';
            isValid = false;
          }

          return errors;
        },
      );

      setErrors((prev: Errors) => ({
        ...prev,
        uniqueThreats: uniqueThreatsErrors,
      }));
    }

    return isValid;
  }, [mode, singleDomainThreats, uniqueDomainThreats]);

  const resetForm = useCallback(() => {
    handleClose();

    setSingleDomainThreats({
      protectedAssetId: '',
      protectedAssetName: '',
      companyId: '',
      threatName: '',
      createThreats: [],
    });

    setUniqueDomainThreats([
      {
        protectedAssetId: '',
        protectedAssetName: '',
        companyId: '',
        threatName: '',
        createThreats: [],
      },
    ]);

    setErrors({
      singleDomain: '',
      singleThreats: '',
      uniqueThreats: [],
    });

    setMode('single');
    setSelectedProcess('w');
    setSelectedStatus('confirmed');
    setSelectedNoteIds([]);
    setCommentText('');
    setFnInformation('');
    setAiSummary(null);
  }, [handleClose]);

  const handleAddThreats = useCallback(() => {
    if (!validateInputs()) return;
    let createThreatRequest = [];
    if (mode === 'single') {
      createThreatRequest = singleDomainThreats?.createThreats?.map(
        (threat: string) => {
          return {
            threat_name: threat,
            status: selectedStatus,
            user_id: userData?.data?.Id || '',
            company_id: singleDomainThreats?.companyId,
            process: selectedProcess,
            priority: 1,
            protected_asset_id: singleDomainThreats?.protectedAssetId,
          };
        },
      );
    } else {
      createThreatRequest =
        uniqueDomainThreats?.flatMap((domain: any) =>
          domain?.createThreats?.map((threat: string) => ({
            threat_name: threat,
            status: selectedStatus,
            user_id: userData?.data?.Id || '',
            company_id: domain?.companyId,
            process: selectedProcess,
            priority: 1,
            protected_asset_id: domain?.protectedAssetId,
          })),
        ) || [];
    }

    const augmentedRequest = createThreatRequest.map((item: any) => ({
      ...item,
      ...(selectedNoteIds.length > 0 && { note_ids: selectedNoteIds }),
      ...(commentText.trim() && {
        comment: {
          text: commentText.trim(),
          is_public: false,
        },
      }),
    }));

    createThreatWithExtras({
      items: augmentedRequest,
      ...(fnInformation.trim() &&
        selectedProcess === falseNegativeSourceCode && {
          fnInformation: fnInformation.trim(),
        }),
      ...(aiSummary && { aiSummary }),
    });
    resetForm();
  }, [
    singleDomainThreats,
    mode,
    uniqueDomainThreats,
    selectedProcess,
    selectedStatus,
    selectedNoteIds,
    commentText,
    fnInformation,
    falseNegativeSourceCode,
    aiSummary,
    createThreatWithExtras,
    resetForm,
    userData?.data?.Id,
    validateInputs,
  ]);

  const handleSingleThreatDomainChange = useCallback(
    (value: any) => {
      if (!value) {
        setSingleDomainThreats({
          ...singleDomainThreats,
          protectedAssetName: '',
        });
      }
      const protectedDomain = protectedDomains.find(
        (item: any) => item.Domain.Name === value,
      );
      if (protectedDomain) {
        setSingleDomainThreats((prev: any) => ({
          ...prev,
          protectedAssetName: value,
          protectedAssetId: protectedDomain?.Domain?.Id,
          companyId: protectedDomain?.Company?.Id,
        }));
      }
    },
    [protectedDomains, singleDomainThreats],
  );

  const handleUniqueThreatDomainChange = useCallback(
    (value: any, index: number) => {
      if (!value) {
        setUniqueDomainThreats((prevThreats: DomainThreats[]) =>
          prevThreats.map((threat, i) =>
            i === index
              ? {
                  ...threat,
                  protectedAssetName: '',
                  protectedAssetId: '',
                  companyId: '',
                }
              : threat,
          ),
        );
        return;
      }
      const protectedDomain = protectedDomains.find(
        (item: any) => item.Domain.Name === value,
      );
      if (protectedDomain) {
        setUniqueDomainThreats((prevThreats: DomainThreats[]) =>
          prevThreats.map((threat: DomainThreats, i: number) =>
            i === index
              ? {
                  ...threat,
                  protectedAssetName: value,
                  protectedAssetId: protectedDomain?.Domain?.Id,
                  companyId: protectedDomain?.Company?.Id,
                }
              : threat,
          ),
        );
      }
    },
    [protectedDomains],
  );

  const handleAddRow = () => {
    setUniqueDomainThreats((prev: DomainThreats[]) => [
      ...prev,
      {
        protectedAssetId: '',
        protectedAssetName: '',
        companyId: '',
        threatName: '',
        createThreats: [],
      },
    ]);
  };

  const handleNotesChange = (noteId: number) => {
    setSelectedNoteIds((prev: number[]) =>
      prev.includes(noteId)
        ? prev.filter((id: number) => id !== noteId)
        : [...prev, noteId],
    );
  };

  const handleRemoveRow = (index: number) => {
    setUniqueDomainThreats((prevThreats: DomainThreats[]) =>
      prevThreats.filter((_, i) => i !== index),
    );

    setErrors((prevErrors: Errors) => ({
      ...prevErrors,
      uniqueThreats: prevErrors.uniqueThreats.filter(
        (_: any, i: number) => i !== index,
      ),
    }));
  };

  const handleCloseModal = () => {
    resetForm();
  };

  const isAddThreatsDisabled = useCallback(() => {
    if (isCreatingThreat) return true;

    const isSingleMode = mode === 'single';

    const isThreatsInvalid = (threats: DomainThreats[]) =>
      threats.some(
        threat =>
          !threat.protectedAssetName || threat.createThreats.length === 0,
      );

    if (isSingleMode) {
      return isThreatsInvalid([singleDomainThreats]);
    }

    return isThreatsInvalid(uniqueDomainThreats);
  }, [mode, singleDomainThreats, uniqueDomainThreats, isCreatingThreat]);

  const renderSingleMode = () => (
    <>
      <StyledTypography>Protected Domain :</StyledTypography>
      <Autocomplete
        size="small"
        value={singleDomainThreats.protectedAssetName}
        onChange={(event, value) => handleSingleThreatDomainChange(value)}
        options={[
          ...new Set(
            protectedDomains
              ?.map((item: any) => item?.Domain?.Name)
              .sort((a: string, b: string) => a.localeCompare(b)) || [],
          ),
        ]}
        filterOptions={(options, { inputValue }) => {
          const lowerInput = inputValue?.toLowerCase() || '';

          return options
            .filter((option: any) => {
              const lowerOption = option.toLowerCase();
              return (
                lowerOption.startsWith(lowerInput) ||
                lowerOption.includes(`-${lowerInput}`)
              );
            })
            .slice(0, 100);
        }}
        openOnFocus
        renderInput={params => (
          <StyledBox>
            {isDomainRef.current && (
              <InfoTypography>
                Showing first 100 domains from the list
              </InfoTypography>
            )}
            <StyledTextField
              {...params}
              placeholder="Type or select the domain from the list"
              error={!!errors.singleDomain}
              onFocus={() => (isDomainRef.current = true)}
              onBlur={() => (isDomainRef.current = false)}
            />
          </StyledBox>
        )}
      />
      <StyledTypography>Threat Names :</StyledTypography>
      <MultiDomainInput
        placeholder="Type a domain and press enter"
        value={singleDomainThreats?.createThreats}
        onChange={newThreats =>
          setSingleDomainThreats({
            ...singleDomainThreats,
            createThreats: newThreats,
          })
        }
        error={!!errors?.singleThreats}
      />
    </>
  );

  const renderUniqueMode = () => {
    const handleDropdownOpen = (index: number) => {
      dropdownStates.current[index] = true;
    };

    const handleDropdownClose = (index: number) => {
      dropdownStates.current[index] = false;
    };

    return (
      <>
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell>Threat Name(s)</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uniqueDomainThreats.map(
                (threat: DomainThreats, index: number) => (
                  <TableRow key={index}>
                    <TableCell sx={{ width: '40%', verticalAlign: 'top' }}>
                      <StyledBox sx={{ width: '100%' }}>
                        <Autocomplete
                          size="small"
                          value={threat.protectedAssetName}
                          onChange={(event, value) =>
                            handleUniqueThreatDomainChange(value, index)
                          }
                          options={[
                            ...new Set(
                              protectedDomains
                                ?.map((item: any) => item?.Domain?.Name)
                                .sort((a: string, b: string) =>
                                  a.localeCompare(b),
                                ) || [],
                            ),
                          ]}
                          filterOptions={(options, { inputValue }) => {
                            const lowerInput = inputValue?.toLowerCase() || '';

                            return options
                              .filter((option: any) => {
                                const lowerOption = option.toLowerCase();
                                return (
                                  lowerOption.startsWith(lowerInput) ||
                                  lowerOption.includes(`-${lowerInput}`)
                                );
                              })
                              .slice(0, 100);
                          }}
                          onOpen={() => handleDropdownOpen(index)}
                          onClose={() => handleDropdownClose(index)}
                          renderInput={params => (
                            <StyledBox sx={{ position: 'relative' }}>
                              {dropdownStates.current[index] && (
                                <InfoBox>
                                  <InfoTypography>
                                    Showing first 100 domains from the list
                                  </InfoTypography>
                                </InfoBox>
                              )}
                              <StyledTextField
                                {...params}
                                placeholder="Type or select the domain from the list"
                                error={!!errors.uniqueThreats[index]?.domain}
                                helperText={errors.uniqueThreats[index]?.domain}
                              />
                            </StyledBox>
                          )}
                        />
                      </StyledBox>
                    </TableCell>
                    <TableCell sx={{ width: '50%' }}>
                      <MultiDomainInput
                        key={index}
                        value={threat.createThreats || []}
                        placeholder="Type a domain and press enter"
                        onChange={newThreats => {
                          setUniqueDomainThreats(prevThreats =>
                            prevThreats.map((threat, i) =>
                              i === index
                                ? { ...threat, createThreats: newThreats }
                                : threat,
                            ),
                          );
                        }}
                        error={!!errors.uniqueThreats[index]?.threats}
                      />
                    </TableCell>

                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <IconButton
                        onClick={() => handleRemoveRow(index)}
                        sx={{ color: 'error.main', fontSize: 30 }}
                        title="Remove Row"
                      >
                        <RemoveCircle />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <StyledButton
            onClick={handleAddRow}
            variant="outlined"
            color="primary"
            label="Add Row"
          />
        </Box>
      </>
    );
  };

  return (
    <Modal open={open} onClose={handleCloseModal}>
      <WideModalWrapper data-testid="create-threat-modal">
        <CloseButton
          data-testid="close-threat-modal"
          onClick={handleCloseModal}
        >
          <HighlightOff />
        </CloseButton>
        <Typography variant="h6" component="h2">
          Add Threat Details
        </Typography>
        <ScrollableContent>
          <RadioGroup
            row
            value={mode}
            onChange={e => setMode(e.target.value as AddThreatsMode)}
            sx={{ mb: 0 }}
          >
            <FormControlLabel
              value="single"
              control={<Radio />}
              label="Single Domain for All"
            />
            <FormControlLabel
              value="unique"
              control={<Radio />}
              label="Unique Domains for Each"
            />
          </RadioGroup>

          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <StyledTypography>Source :</StyledTypography>
              <Select
                size="small"
                value={selectedProcess}
                onChange={e => setSelectedProcess(e.target.value)}
                fullWidth
              >
                {sourceOptions.map(
                  (option: { value: string; label: string; name: string }) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box
                        component="span"
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getSourceColor(option.name),
                          display: 'inline-block',
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      {option.label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <StyledTypography>Status :</StyledTypography>
              <Autocomplete
                size="small"
                options={statusOptions}
                value={selectedStatus}
                onChange={(_, value) => setSelectedStatus(value || 'confirmed')}
                data-testid="status-select"
                renderInput={params => (
                  <StyledTextField {...params} placeholder="Select status" />
                )}
              />
            </Box>
          </Box>

          {mode === 'single' ? renderSingleMode() : renderUniqueMode()}

          <SectionDivider />

          <StyledTypography>Notes (optional) :</StyledTypography>
          <NotesContainer>
            {notesValuesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <Loader size={25} />
              </Box>
            ) : (
              <Grid container spacing={{ xs: 1.5 }}>
                <NoteList
                  handleNotesChange={handleNotesChange}
                  selectedNoteIds={selectedNoteIds}
                />
              </Grid>
            )}
          </NotesContainer>

          <StyledTypography>Comment (optional) :</StyledTypography>
          <Input
            data-testid="create-threat-comment-input"
            multiline
            rows={3}
            placeholder="Write an optional comment to apply to all created threats..."
            value={commentText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCommentText(e.target.value)
            }
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                height: 'auto',
                padding: '8px 14px',
              },
            }}
          />

          {selectedProcess === falseNegativeSourceCode && (
            <>
              <SectionDivider />
              <StyledTypography>False Negative Information :</StyledTypography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mb: 1 }}
              >
                This information will be stored internally and will not be
                visible to customers.
              </Typography>
              <Input
                data-testid="fn-information-input"
                multiline
                rows={3}
                placeholder="Provide context about why this is a false negative..."
                value={fnInformation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFnInformation(e.target.value)
                }
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: 'auto',
                    padding: '8px 14px',
                  },
                }}
              />
            </>
          )}

          <BulkExplainSection
            source="bulk_home"
            payload={bulkExplainPayload}
            protectedAssetId={bulkExplainIds.protectedAssetId}
            companyId={bulkExplainIds.companyId}
            onSummaryChange={setAiSummary}
          />
        </ScrollableContent>

        <ButtonWrapper>
          <Button
            data-testid="cancel-threat-modal"
            onClick={handleCloseModal}
            variant="outlined"
            color="secondary"
            label="Cancel"
          />
          <GradientButton
            data-testid="submit-threat"
            onClick={handleAddThreats}
            label="Add Threats"
            type="submit"
            disabled={isAddThreatsDisabled()}
          />
        </ButtonWrapper>
      </WideModalWrapper>
    </Modal>
  );
};

export default CreateThreatModel;
